import { formatPartNumber } from '../functions/formatPartNumber';
import type { InventoryItemEntry } from '../types/inventory';

/** Local editor row (value may be mid-edit before strip). */
export interface PartNumberEditorRow {
    key: string;
    value: string;
    isPrimary: boolean;
}

/** Normalized API write shape for part numbers. */
export interface NormalizedEquivalentPartNumbers {
    /** Primary stripped PN. */
    strippedManufacturerPartNumber: string;
    /** Full set including primary. */
    equivalentPartNumbers: Array<string>;
}

let partNumberRowKeySeq = 0;

export function nextPartNumberRowKey(): string {
    partNumberRowKeySeq += 1;
    return `pn-${partNumberRowKeySeq}`;
}

export function createEmptyPartNumberRows(): PartNumberEditorRow[] {
    return [{ key: nextPartNumberRowKey(), value: '', isPrimary: true }];
}

/** Map API read fields → editor rows (primary marked via strippedManufacturerPartNumber). */
export function equivalentPartNumbersToEditorRows(
    equivalentPartNumbers: Array<string>,
    primary: string,
): PartNumberEditorRow[] {
    const primaryStripped = formatPartNumber(primary);
    const values = equivalentPartNumbers
        .map((v) => formatPartNumber(v))
        .filter(Boolean);
    const unique: string[] = [];
    const seen = new Set<string>();
    for (const v of values) {
        if (seen.has(v)) continue;
        seen.add(v);
        unique.push(v);
    }
    if (primaryStripped && !seen.has(primaryStripped)) {
        unique.unshift(primaryStripped);
    }
    if (!unique.length) return createEmptyPartNumberRows();

    const primaryValue =
        primaryStripped && unique.includes(primaryStripped)
            ? primaryStripped
            : unique[0];

    return unique.map((value) => ({
        key: nextPartNumberRowKey(),
        value,
        isPrimary: value === primaryValue,
    }));
}

/** Primary stripped PN from an inventory item. */
export function getPrimaryPartNumber(
    item: Pick<
        InventoryItemEntry,
        'equivalentPartNumbers' | 'strippedManufacturerPartNumber'
    >,
): string {
    if (item.strippedManufacturerPartNumber) {
        return formatPartNumber(item.strippedManufacturerPartNumber);
    }
    return formatPartNumber(item.equivalentPartNumbers?.[0] ?? '');
}

export function getAlternateCount(
    item: Pick<
        InventoryItemEntry,
        'equivalentPartNumbers' | 'strippedManufacturerPartNumber'
    >,
): number {
    const primary = getPrimaryPartNumber(item);
    const all = (item.equivalentPartNumbers ?? [])
        .map((v) => formatPartNumber(v))
        .filter(Boolean);
    const unique = new Set(all);
    if (primary) unique.add(primary);
    return Math.max(0, unique.size - 1);
}

export function getPrimaryFromEditorRows(rows: Array<PartNumberEditorRow>): string {
    const primary = rows.find((r) => r.isPrimary)?.value ?? rows[0]?.value ?? '';
    return formatPartNumber(primary);
}

/**
 * Normalize editor rows to API equivalent + primary fields.
 * Returns null when invalid (empty, no primary, or duplicate stripped values).
 */
export function normalizePartNumberRows(
    rows: Array<PartNumberEditorRow>,
): NormalizedEquivalentPartNumbers | null {
    const equivalentPartNumbers: string[] = [];
    const seen = new Set<string>();
    let primary: string | null = null;

    for (const row of rows) {
        const value = formatPartNumber(row.value);
        if (!value) continue;
        if (seen.has(value)) return null;
        seen.add(value);
        equivalentPartNumbers.push(value);
        if (row.isPrimary) primary = value;
    }

    if (equivalentPartNumbers.length === 0) return null;
    if (!primary) primary = equivalentPartNumbers[0];

    return {
        strippedManufacturerPartNumber: primary,
        equivalentPartNumbers,
    };
}

export function equivalentPartNumbersEqual(
    a: NormalizedEquivalentPartNumbers,
    b: Pick<
        InventoryItemEntry,
        'equivalentPartNumbers' | 'strippedManufacturerPartNumber'
    >,
): boolean {
    const aPrimary = formatPartNumber(a.strippedManufacturerPartNumber);
    const bPrimary = formatPartNumber(b.strippedManufacturerPartNumber ?? '');
    if (aPrimary !== bPrimary) return false;

    const as = [...a.equivalentPartNumbers].map(formatPartNumber).filter(Boolean).sort();
    const bs = [...(b.equivalentPartNumbers ?? [])]
        .map(formatPartNumber)
        .filter(Boolean)
        .sort();
    if (as.length !== bs.length) return false;
    return as.every((v, i) => v === bs[i]);
}

export function editorRowsFingerprint(rows: Array<PartNumberEditorRow>): string {
    return rows
        .map((r) => `${r.isPrimary ? '1' : '0'}:${formatPartNumber(r.value)}`)
        .join('|');
}
